const REASON_LINES = {
  missing_calendar: "Collect a contact and hand the lead to the operator.",
  calendar_capability_unproven: "Calendar capability is not proven.",
  confirmation_operator_assisted: "Confirmation stays with the operator.",
  model_nlu_not_admitted: "Model NLU is not admitted.",
  automatic_booking_not_admitted: "Automatic booking is not admitted.",
};

export function reasonCode(reason) {
  if (typeof reason === "string" && reason !== "") {
    return reason;
  }
  if (reason && typeof reason.code === "string" && reason.code !== "") {
    return reason.code;
  }
  return "";
}

export function reasonLine(reason) {
  const code = reasonCode(reason);
  if (!code) {
    return "not configured";
  }
  return REASON_LINES[code] || code;
}

export function reportsNoFreeSlotsClosed(mode) {
  return Boolean(mode) && mode.reports_no_free_slots === false;
}

export function derivedModeAccepted(mode) {
  if (!mode || mode.shows_automatic !== false) {
    return null;
  }
  if (!reportsNoFreeSlotsClosed(mode)) {
    return null;
  }
  if (mode.booking_mode !== "operator_assisted") {
    return null;
  }
  if (mode.model_nlu_enabled !== false) {
    return null;
  }
  if (!Array.isArray(mode.reasons)) {
    return null;
  }
  const codes = mode.reasons.map(reasonCode);
  if (
    !codes.includes("model_nlu_not_admitted")
    || !codes.includes("automatic_booking_not_admitted")
  ) {
    return null;
  }
  return mode;
}

export function readinessNotice() {
  return "Readiness is not evaluated.";
}

export function readinessCheckTimeNotice() {
  return "No readiness check time is recorded.";
}

export function readinessExpiryNotice() {
  return "No readiness expiry is recorded.";
}

export function readinessCandidateNotice() {
  return "No readiness candidate hash is recorded.";
}

export function readinessAvailabilityNotice() {
  return "A readiness read does not promise provider availability.";
}

export function readinessChatNotice() {
  return "A readiness read does not move an existing v2 chat to v1.";
}

export function readinessSnapshotNotice() {
  return "A readiness display is a snapshot, not a standing permission.";
}

export function readinessProviderNotice() {
  return "This readiness result does not read provider status.";
}

export function readinessAuditNotice() {
  return "This screen does not record a readiness audit.";
}

export function readinessMissingNotice() {
  return "A missing readiness result is not a successful check.";
}

export function readinessAdmissionNotice() {
  return "This readiness result is not a final admission.";
}

export function readinessRegisterNotice() {
  return "This readiness result is not written to the capability register.";
}

export function readinessDistinctionNotice() {
  return "This readiness result keeps invalid settings, an unavailable dependency, a capability that is not approved, and a runtime permission separate from rollout authorization.";
}

export function readinessBypassNotice() {
  return "This readiness result does not treat operator-assisted mode as a bypass of tenant isolation, KMS, an incomplete privacy restore ledger, or an unacceptable data profile.";
}

export function readinessPauseNotice() {
  return "This readiness result does not turn paused, takeover, or opt-out into automatic mode from an earlier check.";
}

export function readinessPendingLines() {
  return [
    "widget confirmation: pending",
    "operator coverage: pending",
    "lifecycle boundary: pending",
    "rollout authorization: pending",
  ];
}

const READINESS_INPUTS = [
  ["widget confirmation", "pending"],
  ["operator coverage", "pending"],
  ["lifecycle boundary", "pending"],
  ["rollout authorization", "pending"],
];

function readinessRecordKeys(value, expected) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const keys = Object.keys(value);
  return keys.length === expected.length && expected.every((key) => keys.includes(key));
}

export function readinessLinesFromResult(body) {
  if (!readinessRecordKeys(body, ["status", "checked_at", "expires_at", "candidate_hash", "inputs"])) {
    return null;
  }
  if (
    body.status !== "not_evaluated"
    || body.checked_at !== null
    || body.expires_at !== null
    || body.candidate_hash !== null
    || !Array.isArray(body.inputs)
    || body.inputs.length !== READINESS_INPUTS.length
  ) {
    return null;
  }
  const lines = [];
  for (let index = 0; index < READINESS_INPUTS.length; index += 1) {
    const item = body.inputs[index];
    const [name, status] = READINESS_INPUTS[index];
    if (!readinessRecordKeys(item, ["name", "status"])) {
      return null;
    }
    if (item.name !== name || item.status !== status) {
      return null;
    }
    lines.push(`${name}: ${status}`);
  }
  const encoded = JSON.stringify(body);
  if (encoded.includes("ready") || encoded.includes("phase_5_accepted")) {
    return null;
  }
  return lines;
}

export function nextReadinessLines(previous, settingsWrite, body) {
  if (settingsWrite === "accepted") {
    return readinessLinesFromResult(body);
  }
  return previous;
}
